import React, { useState, useEffect, useRef } from "react";
import { 
  X, 
  Plus, 
  Trash2, 
  Upload, 
  Loader2, 
  CheckCircle2,
  Dumbbell
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { fileUploadAPI } from "../../services/api";
import VideoPlayer from "../common/VideoPlayer";

const ExerciseModal = ({
  isOpen,
  onClose,
  onSubmit,
  exercise,
  title,
  loading = false,
}) => {
  const { colors } = useTheme();

  // State definitions
  const [formData, setFormData] = useState({
    name: "",
    notes: "",
    category: "",
    difficulty: "beginner",
    bodyParts: [],
    equipment: [],
    instructions: "",
    sets: "",
    reps: "",
    duration: "",
    restTime: "",
    mediaUrls: [],
    tags: [],
    isActive: true,
    reviewStatus: "review_needed",
  });
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState("basic");
  const [newBodyPart, setNewBodyPart] = useState("");
  const [newEquipment, setNewEquipment] = useState("");
  const [newMediaUrl, setNewMediaUrl] = useState("");
  const [newTag, setNewTag] = useState("");
  const [uploading, setUploading] = useState(false);

  const mediaFileInputRef = useRef(null);

  // Constants
  const tabs = [
    { id: "basic", name: "Temel Bilgiler" },
    { id: "details", name: "Egzersiz Detayları" },
    { id: "media", name: "Medya ve Etiketler" },
  ];

  const commonBodyParts = [
    "Göğüs", "Sırt", "Omuzlar", "Kollar", "Biseps", "Triseps", "Bacaklar", 
    "Kuadriseps", "Hamstring", "Kalça", "Baldır", "Gövde", "Karın", "Tüm Vücut"
  ];

  const commonEquipment = [
    "Dambıl", "Barbell", "Kettlebell", "Direnç Bantları", "Barfiks Barı", 
    "Sehpa", "Sağlık Topu", "Kablo Makinesi", "Vücut Ağırlığı", "Yok"
  ];

  // Initialize data
  useEffect(() => {
    if (exercise) {
      setFormData({
        name: exercise.name || "",
        notes: exercise.notes || "",
        category: exercise.category || "",
        difficulty: exercise.difficulty || "beginner",
        bodyParts: exercise.bodyParts || [],
        equipment: exercise.equipment || [],
        instructions: exercise.instructions || "",
        sets: exercise.sets ? exercise.sets.toString() : "",
        reps: exercise.reps ? exercise.reps.toString() : "",
        duration: exercise.duration ? exercise.duration.toString() : "",
        restTime: exercise.restTime ? exercise.restTime.toString() : "",
        mediaUrls: exercise.mediaUrls || [],
        tags: exercise.tags || [],
        isActive: exercise.isActive !== undefined ? exercise.isActive : true,
        reviewStatus: exercise.reviewStatus || "review_needed",
      });
    } else {
      setFormData({
        name: "",
        notes: "",
        category: "",
        difficulty: "beginner",
        bodyParts: [],
        equipment: [],
        instructions: "",
        sets: "",
        reps: "",
        duration: "",
        restTime: "",
        mediaUrls: [],
        tags: [],
        isActive: true,
        reviewStatus: "review_needed",
      });
    }
    setErrors({});
    setActiveTab("basic");
    setNewBodyPart("");
    setNewEquipment("");
    setNewMediaUrl("");
    setNewTag("");
    setUploading(false);
  }, [exercise, isOpen]);

  // Validation
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Egzersiz adı gereklidir";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const submitData = {
        ...formData,
        sets: formData.sets ? parseInt(formData.sets) : null,
        reps: formData.reps ? parseInt(formData.reps) : null,
        duration: formData.duration ? parseInt(formData.duration) : null,
        restTime: formData.restTime ? parseInt(formData.restTime) : null,
      };

      Object.keys(submitData).forEach((key) => {
        if (submitData[key] === "") submitData[key] = null;
      });

      await onSubmit(submitData);
    } catch (error) {
      console.error("Error submitting:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const addBodyPart = (part) => {
    const p = part || newBodyPart;
    if (p.trim() && !formData.bodyParts.includes(p.trim())) {
      setFormData((prev) => ({
        ...prev,
        bodyParts: [...prev.bodyParts, p.trim()],
      }));
      setNewBodyPart("");
    }
  };

  const removeBodyPart = (index) => {
    setFormData((prev) => ({
      ...prev,
      bodyParts: prev.bodyParts.filter((_, i) => i !== index),
    }));
  };

  const addEquipment = (equip) => {
    const e = equip || newEquipment;
    if (e.trim() && !formData.equipment.includes(e.trim())) {
      setFormData((prev) => ({
        ...prev,
        equipment: [...prev.equipment, e.trim()],
      }));
      setNewEquipment("");
    }
  };

  const removeEquipment = (index) => {
    setFormData((prev) => ({
      ...prev,
      equipment: prev.equipment.filter((_, i) => i !== index),
    }));
  };

  const addMediaUrl = () => {
    if (newMediaUrl.trim() && !formData.mediaUrls.includes(newMediaUrl.trim())) {
      setFormData((prev) => ({ ...prev, mediaUrls: [...prev.mediaUrls, newMediaUrl.trim()] }));
      setNewMediaUrl("");
    }
  };

  const removeMediaUrl = (index) => {
    setFormData((prev) => ({ ...prev, mediaUrls: prev.mediaUrls.filter((_, i) => i !== index) }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData((prev) => ({ ...prev, tags: [...prev.tags, newTag.trim()] }));
      setNewTag("");
    }
  };

  const removeTag = (index) => {
    setFormData((prev) => ({ ...prev, tags: prev.tags.filter((_, i) => i !== index) }));
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const response = await fileUploadAPI.getPresignedUrl(file.type);
      const { uploadUrl, imageUrl } = response.data.data;
      await fileUploadAPI.uploadFile(uploadUrl, file, file.type);
      setFormData((prev) => ({ ...prev, mediaUrls: [...prev.mediaUrls, imageUrl] }));
    } catch (error) {
      console.error("File upload error:", error);
    } finally {
      setUploading(false);
    }
  };

  const isVideo = (url) => url?.match(/\.(mp4|webm|ogg|mov)$/i) || url?.includes("video");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4">
      <div className={`w-full max-w-4xl bg-white dark:bg-[#121214] rounded-2xl shadow-2xl flex flex-col border border-zinc-200 dark:border-zinc-800 max-h-[85vh] overflow-hidden`}>
        {/* Simple Header */}
        <div className="flex items-center justify-between p-6 border-b border-zinc-100 dark:border-zinc-800 shrink-0">
          <div className="flex items-center space-x-3">
            <h2 className={`text-xl font-bold ${colors.textPrimary}`}>
              {title}
            </h2>
          </div>
          <button onClick={onClose} className={`p-2 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors`}>
            <X className={`w-5 h-5 ${colors.textMuted}`} />
          </button>
        </div>

        {/* Clean Tabs */}
        <div className="flex px-6 border-b border-zinc-100 dark:border-zinc-800 shrink-0">
          {tabs.map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-4 text-sm font-semibold transition-all relative ${isActive ? 'text-blue-600 dark:text-blue-500' : `${colors.textMuted} hover:text-zinc-600`}`}
              >
                {tab.name}
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-600 dark:bg-blue-500 rounded-full" />
                )}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 scrollbar-thin scrollbar-thumb-zinc-200 dark:scrollbar-thumb-zinc-800">
          <div className="space-y-6">
            {activeTab === "basic" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2 space-y-1.5">
                  <label className={`text-sm font-medium ${colors.textSecondary}`}>Egzersiz Adı *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Egzersiz adını giriniz"
                    className={`w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border ${errors.name ? 'border-red-500' : 'border-zinc-300 dark:border-zinc-700'} rounded-lg focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow`}
                  />
                  {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                </div>

                <div className="space-y-1.5">
                  <label className={`text-sm font-medium ${colors.textSecondary}`}>Kategori</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg outline-none focus:border-blue-500`}
                  >
                    <option value="">Seçiniz</option>
                    <option value="strength">Kuvvet</option>
                    <option value="cardio">Kardiyo</option>
                    <option value="flexibility">Esneklik</option>
                    <option value="functional">Fonksiyonel</option>
                  </select>
                </div>

                <div className="space-y-1.5">
                  <label className={`text-sm font-medium ${colors.textSecondary}`}>Zorluk</label>
                  <select
                    name="difficulty"
                    value={formData.difficulty}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg outline-none focus:border-blue-500`}
                  >
                    <option value="beginner">Başlangıç</option>
                    <option value="intermediate">Orta</option>
                    <option value="advanced">İleri</option>
                  </select>
                </div>

                <div className="md:col-span-2 space-y-1.5">
                  <label className={`text-sm font-medium ${colors.textSecondary}`}>Talimatlar</label>
                  <textarea
                    name="instructions"
                    value={formData.instructions}
                    onChange={handleChange}
                    rows={4}
                    placeholder="Uygulama talimatlarını buraya yazın..."
                    className={`w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg outline-none focus:border-blue-500 resize-none`}
                  />
                </div>

                <div className="md:col-span-2 space-y-1.5">
                  <label className={`text-sm font-medium ${colors.textSecondary}`}>Notlar</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleChange}
                    rows={2}
                    placeholder="Ek notlar..."
                    className={`w-full px-4 py-2.5 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg outline-none focus:border-blue-500 resize-none`}
                  />
                </div>

                <div className="md:col-span-2 flex items-center justify-between p-4 bg-zinc-50 dark:bg-zinc-900 rounded-xl border border-zinc-100 dark:border-zinc-800">
                  <div className="flex items-center space-x-3">
                    <label className={`text-sm font-medium ${colors.textSecondary}`}>İnceleme Durumu:</label>
                    <select
                      name="reviewStatus"
                      value={formData.reviewStatus}
                      onChange={handleChange}
                      className="bg-transparent font-semibold text-sm outline-none text-blue-600 dark:text-blue-400"
                    >
                      <option value="review_needed">İnceleme Bekliyor</option>
                      <option value="approved">Onaylı</option>
                      <option value="rejected">Reddedildi</option>
                    </select>
                  </div>
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input type="checkbox" name="isActive" checked={formData.isActive} onChange={handleChange} className="w-4 h-4 rounded border-zinc-300 text-blue-600 focus:ring-blue-500" />
                    <span className={`text-sm font-medium ${colors.textSecondary}`}>Aktif</span>
                  </label>
                </div>
              </div>
            )}

            {activeTab === "details" && (
              <div className="space-y-8">
                <section className="space-y-4">
                  <h3 className={`text-sm font-bold uppercase tracking-wider ${colors.textMuted}`}>Egzersiz Parametreleri</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[['sets', 'Set'], ['reps', 'Tekrar'], ['duration', 'Süre (sn)'], ['restTime', 'Dinlenme (sn)']].map(([key, label]) => (
                      <div key={key} className="space-y-1.5">
                        <label className={`text-xs font-semibold ${colors.textTertiary}`}>{label}</label>
                        <input
                          type="number"
                          name={key}
                          value={formData[key]}
                          onChange={handleChange}
                          className="w-full px-3 py-2 bg-white dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg outline-none focus:border-blue-500 text-center font-bold"
                        />
                      </div>
                    ))}
                  </div>
                </section>

                <section className="space-y-4">
                  <h3 className={`text-sm font-bold uppercase tracking-wider ${colors.textMuted}`}>Hedef Vücut Bölgeleri</h3>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                        {commonBodyParts.map(part => (
                          <button
                            key={part}
                            onClick={() => formData.bodyParts.includes(part) ? removeBodyPart(formData.bodyParts.indexOf(part)) : addBodyPart(part)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${formData.bodyParts.includes(part) ? 'bg-blue-600 border-blue-600 text-white' : 'border-zinc-200 dark:border-zinc-800 text-zinc-500 hover:border-zinc-400'}`}
                          >
                            {part}
                          </button>
                        ))}
                    </div>
                  </div>
                </section>

                <section className="space-y-4">
                  <h3 className={`text-sm font-bold uppercase tracking-wider ${colors.textMuted}`}>Gerekli Ekipman</h3>
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-2">
                        {commonEquipment.map(equip => (
                          <button
                            key={equip}
                            onClick={() => formData.equipment.includes(equip) ? removeEquipment(formData.equipment.indexOf(equip)) : addEquipment(equip)}
                            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${formData.equipment.includes(equip) ? 'bg-zinc-800 dark:bg-zinc-200 border-zinc-800 dark:border-zinc-200 text-white dark:text-zinc-900' : 'border-zinc-200 dark:border-zinc-800 text-zinc-500 hover:border-zinc-400'}`}
                          >
                            {equip}
                          </button>
                        ))}
                    </div>
                  </div>
                </section>
              </div>
            )}

            {activeTab === "media" && (
              <div className="space-y-8">
                <section className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className={`text-sm font-bold uppercase tracking-wider ${colors.textMuted}`}>Medya Dosyaları</h3>
                    <label className="cursor-pointer px-4 py-2 bg-zinc-100 dark:bg-zinc-800 rounded-lg text-xs font-bold hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors flex items-center space-x-2">
                      {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                      <span>{uploading ? 'Yükleniyor...' : 'Dosya Yükle'}</span>
                      <input type="file" accept="image/*,video/*" className="hidden" onChange={handleFileChange} disabled={uploading} />
                    </label>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {formData.mediaUrls.map((url, index) => (
                      <div key={index} className="aspect-video rounded-xl bg-zinc-100 dark:bg-zinc-900 overflow-hidden relative group border border-zinc-200 dark:border-zinc-700">
                        {isVideo(url) ? (
                          <div className="w-full h-full flex items-center justify-center bg-black">
                            <VideoPlayer src={url} className="w-full h-full object-contain" />
                          </div>
                        ) : (
                          <img src={url} alt="" className="w-full h-full object-cover" />
                        )}
                        <button onClick={() => removeMediaUrl(index)} className="absolute top-2 right-2 p-1.5 bg-red-600 text-white rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="space-y-3">
                   <h3 className={`text-sm font-bold uppercase tracking-wider ${colors.textMuted}`}>Harici Bağlantı</h3>
                   <div className="flex space-x-2">
                      <input
                        type="url"
                        value={newMediaUrl}
                        onChange={(e) => setNewMediaUrl(e.target.value)}
                        placeholder="Video veya resim URL'si"
                        className="flex-1 px-4 py-2 bg-zinc-50 dark:bg-zinc-900 border border-zinc-300 dark:border-zinc-700 rounded-lg outline-none text-sm"
                      />
                      <button onClick={addMediaUrl} className="px-4 py-2 bg-zinc-800 dark:bg-zinc-200 text-white dark:text-zinc-900 rounded-lg text-xs font-bold hover:opacity-90 transition-opacity">Ekle</button>
                   </div>
                </section>

                <section className="space-y-4 pt-4 border-t border-zinc-100 dark:border-zinc-800">
                   <h3 className={`text-sm font-bold uppercase tracking-wider ${colors.textMuted}`}>Etiketler</h3>
                   <div className="flex flex-wrap gap-2">
                    {formData.tags.map((tag, index) => (
                      <div key={index} className="flex items-center space-x-2 px-2.5 py-1 bg-zinc-100 dark:bg-zinc-800 rounded-lg text-xs font-semibold">
                        <span>{tag}</span>
                        <button onClick={() => removeTag(index)} className="text-zinc-400 hover:text-red-500"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={newTag}
                        onChange={(e) => setNewTag(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                        placeholder="Yeni Etiket..."
                        className="bg-transparent text-xs font-medium outline-none border-b border-zinc-300 dark:border-zinc-700 px-1 w-24"
                      />
                      <button onClick={addTag} className="p-1 hover:text-blue-500"><Plus className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </section>
              </div>
            )}
          </div>
        </div>

        {/* Global Action Footer */}
        <div className="p-6 border-t border-zinc-100 dark:border-zinc-800 flex justify-end space-x-3 shrink-0 bg-white dark:bg-[#121214]">
          <button 
            type="button" 
            onClick={onClose} 
            className="px-6 py-2.5 rounded-lg border border-zinc-300 dark:border-zinc-700 text-sm font-bold hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || !formData.name}
            className={`px-8 py-2.5 rounded-lg bg-blue-600 dark:bg-blue-500 text-white text-sm font-bold hover:opacity-90 transition-opacity flex items-center space-x-2 disabled:opacity-50`}
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            <span>{exercise?.id ? 'Egzersizi Güncelle' : 'Egzersiz Oluştur'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExerciseModal;
